// Demo data for showcasing OmniWorth features without authentication

export const DEMO_NET_WORTH = {
    netWorth: 287450,
    totalAssets: 312450,
    totalLiabilities: 25000,
    cashBalance: 42500,
    investmentBalance: 245950,
    creditBalance: 3200,
    loanBalance: 21800,
    change: {
        amount: 4230,
        percentage: 1.49,
        period: "yesterday"
    },
    calculatedAt: new Date().toISOString()
};

// Generate 1 year of net worth history ending today
// Starting from ~$220,000 a year ago, growing to current $287,450
function generateNetWorthHistory() {
    const history = [];
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    const startDate = new Date(endDate);
    startDate.setFullYear(startDate.getFullYear() - 1);
    const startNetWorth = 220000;
    const endNetWorth = 287450;
    const startAssets = 248000;
    const endAssets = 312450;
    const startLiabilities = 28000;
    const endLiabilities = 25000;

    // Generate data points every 2-3 days for a year
    let currentDate = new Date(startDate);
    let dayIndex = 0;
    const totalDays = 365;

    while (currentDate <= endDate) {
        const progress = dayIndex / totalDays;

        // Base values with overall trend
        const baseNW = startNetWorth + (endNetWorth - startNetWorth) * progress;
        const baseAssets = startAssets + (endAssets - startAssets) * progress;
        const baseLiabilities = startLiabilities + (endLiabilities - startLiabilities) * progress;

        // Add realistic volatility (±2% variation using deterministic sin/cos)
        const volatility = baseNW * 0.02 * (Math.sin(dayIndex * 0.3) + Math.cos(dayIndex * 0.17) * 0.5);

        // Seasonal patterns (market dip Sept/Oct, rally Dec/Jan)
        const month = currentDate.getMonth();
        let seasonalAdjust = 0;
        if (month === 8 || month === 9) seasonalAdjust = -baseNW * 0.015;
        if (month === 11 || month === 0) seasonalAdjust = baseNW * 0.01;

        const netWorth = Math.round(baseNW + volatility + seasonalAdjust);
        const assets = Math.round(baseAssets + volatility + seasonalAdjust);
        const liabilities = Math.round(baseLiabilities - volatility * 0.1);

        history.push({
            date: currentDate.toISOString().split('T')[0],
            netWorth,
            assets,
            liabilities
        });

        // Move forward 2-3 days
        const daysToAdd = dayIndex % 3 === 0 ? 3 : 2;
        currentDate.setDate(currentDate.getDate() + daysToAdd);
        dayIndex += daysToAdd;
    }

    // Ensure the last entry is exactly the current values
    if (history.length > 0) {
        history[history.length - 1] = {
            date: endDate.toISOString().split('T')[0],
            netWorth: endNetWorth,
            assets: endAssets,
            liabilities: endLiabilities
        };
    }

    return history;
}

export const DEMO_NET_WORTH_HISTORY = generateNetWorthHistory();

export const DEMO_ACCOUNTS = [
    {
        accountId: "demo-checking",
        name: "Primary Checking",
        type: "DEPOSITORY",
        subtype: "checking",
        mask: "4521",
        balances: { current: 8500, available: 8500 }
    },
    {
        accountId: "demo-savings",
        name: "High Yield Savings",
        mask: "7832",
        type: "DEPOSITORY",
        subtype: "savings",
        balances: { current: 34000, available: 34000 }
    },
    {
        accountId: "demo-401k",
        name: "Fidelity 401(k)",
        type: "INVESTMENT",
        subtype: "401k",
        mask: "9012",
        balances: { current: 145000 }
    },
    {
        accountId: "demo-roth",
        name: "Vanguard Roth IRA",
        type: "INVESTMENT",
        subtype: "roth",
        mask: "3456",
        balances: { current: 67500 }
    },
    {
        accountId: "demo-brokerage",
        name: "Schwab Brokerage",
        type: "INVESTMENT",
        subtype: "brokerage",
        mask: "6789",
        balances: { current: 33450 }
    },
    {
        accountId: "demo-credit",
        name: "Chase Sapphire",
        type: "CREDIT",
        subtype: "credit card",
        mask: "2345",
        balances: { current: 3200 }
    },
    {
        accountId: "demo-student",
        name: "Student Loans",
        type: "LOAN",
        subtype: "student",
        mask: "8901",
        balances: { current: 21800 }
    },
];

export const DEMO_HOLDINGS = [
    {
        accountId: "demo-401k",
        tickerSymbol: "VTI",
        name: "Vanguard Total Stock Market ETF",
        securityId: "vti-1",
        quantity: 180,
        costBasis: 32400,
        marketValue: 45000,
        gainLoss: 12600,
        closePrice: 250.00,
        costShare: 180.00
    },
    {
        accountId: "demo-401k",
        tickerSymbol: "VXUS",
        name: "Vanguard Total International Stock ETF",
        securityId: "vxus-1",
        quantity: 400,
        costBasis: 20000,
        marketValue: 24000,
        gainLoss: 4000,
        closePrice: 60.00,
        costShare: 50.00
    },
    {
        accountId: "demo-401k",
        tickerSymbol: "BND",
        name: "Vanguard Total Bond Market ETF",
        securityId: "bnd-1",
        quantity: 500,
        costBasis: 38000,
        marketValue: 37500,
        gainLoss: -500,
        closePrice: 75.00,
        costShare: 76.00
    },
    {
        accountId: "demo-401k",
        tickerSymbol: "VGSLX",
        name: "Vanguard Real Estate Index Fund",
        securityId: "vgslx-1",
        quantity: 250,
        costBasis: 30000,
        marketValue: 38500,
        gainLoss: 8500,
        closePrice: 154.00,
        costShare: 120.00
    },
    {
        accountId: "demo-roth",
        tickerSymbol: "VOO",
        name: "Vanguard S&P 500 ETF",
        securityId: "voo-1",
        quantity: 100,
        costBasis: 35000,
        marketValue: 45000,
        gainLoss: 10000,
        closePrice: 450.00,
        costShare: 350.00
    },
    {
        accountId: "demo-roth",
        tickerSymbol: "AAPL",
        name: "Apple Inc.",
        securityId: "aapl-1",
        quantity: 50,
        costBasis: 7500,
        marketValue: 11250,
        gainLoss: 3750,
        closePrice: 225.00,
        costShare: 150.00
    },
    {
        accountId: "demo-roth",
        tickerSymbol: "MSFT",
        name: "Microsoft Corporation",
        securityId: "msft-1",
        quantity: 25,
        costBasis: 8000,
        marketValue: 11250,
        gainLoss: 3250,
        closePrice: 450.00,
        costShare: 320.00
    },
    {
        accountId: "demo-brokerage",
        tickerSymbol: "NVDA",
        name: "NVIDIA Corporation",
        securityId: "nvda-1",
        quantity: 30,
        costBasis: 12000,
        marketValue: 18000,
        gainLoss: 6000,
        closePrice: 600.00,
        costShare: 400.00
    },
    {
        accountId: "demo-brokerage",
        tickerSymbol: "GOOGL",
        name: "Alphabet Inc.",
        securityId: "googl-1",
        quantity: 50,
        costBasis: 7000,
        marketValue: 9250,
        gainLoss: 2250,
        closePrice: 185.00,
        costShare: 140.00
    },
    {
        accountId: "demo-brokerage",
        tickerSymbol: "AMZN",
        name: "Amazon.com Inc.",
        securityId: "amzn-1",
        quantity: 30,
        costBasis: 4500,
        marketValue: 6200,
        gainLoss: 1700,
        closePrice: 206.67,
        costShare: 150.00
    },
];

export const DEMO_GOALS = [
    {
        id: "goal-1",
        name: "Emergency Fund",
        targetAmount: 30000,
        currentAmount: 34000,
        targetDate: null,
        trackType: "CASH",
        icon: "emergency",
        progressPercent: 113
    },
    {
        id: "goal-2",
        name: "House Down Payment",
        targetAmount: 100000,
        currentAmount: 42500,
        targetDate: "2027-06-01",
        trackType: "MANUAL",
        icon: "house",
        progressPercent: 42.5
    },
    {
        id: "goal-3",
        name: "Retirement by 55",
        targetAmount: 2000000,
        currentAmount: 245950,
        targetDate: "2046-01-01",
        trackType: "INVESTMENTS",
        icon: "retirement",
        progressPercent: 12.3
    },
    {
        id: "goal-4",
        name: "Pay Off Student Loans",
        targetAmount: 45000,
        currentAmount: 23200,
        targetDate: "2028-12-31",
        trackType: "MANUAL",
        icon: "debt",
        progressPercent: 51.6
    },
];

export const DEMO_EMERGENCY_FUND = {
    status: "HEALTHY",
    monthsCovered: 8.5,
    cashBalance: 34000,
    monthlyExpenses: 4000,
};

export const DEMO_PROJECTIONS_INITIAL = {
    investmentBalance: 245950,
    netWorth: 287450,
};

export const DEMO_CONNECTION_HEALTH = {
    summary: {
        totalConnections: 4,
        healthyConnections: 4,
        needsAttention: 0,
        staleConnections: 0,
        oldestSync: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        newestSync: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 min ago
        hasIssues: false
    },
    links: [
        {
            id: 1,
            institutionDisplayName: "Chase",
            institutionLogoUrl: null,
            institutionId: "ins_3",
            connectionStatus: "HEALTHY",
            lastSuccessfulSyncAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
            lastErrorCode: null,
            lastErrorMessage: null,
            active: true
        },
        {
            id: 2,
            institutionDisplayName: "Fidelity",
            institutionLogoUrl: null,
            institutionId: "ins_7",
            connectionStatus: "HEALTHY",
            lastSuccessfulSyncAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
            lastErrorCode: null,
            lastErrorMessage: null,
            active: true
        },
        {
            id: 3,
            institutionDisplayName: "Vanguard",
            institutionLogoUrl: null,
            institutionId: "ins_19",
            connectionStatus: "HEALTHY",
            lastSuccessfulSyncAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
            lastErrorCode: null,
            lastErrorMessage: null,
            active: true
        },
        {
            id: 4,
            institutionDisplayName: "Charles Schwab",
            institutionLogoUrl: null,
            institutionId: "ins_10",
            connectionStatus: "HEALTHY",
            lastSuccessfulSyncAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            lastErrorCode: null,
            lastErrorMessage: null,
            active: true
        }
    ]
};

export const DEMO_TRANSACTIONS = {
    "demo-checking": [
        { transactionId: "tx-1", accountId: "demo-checking", date: "2026-01-12", name: "Whole Foods Market", amount: 127.43, category: "Groceries" },
        { transactionId: "tx-2", accountId: "demo-checking", date: "2026-01-11", name: "Shell Gas Station", amount: 52.18, category: "Transportation" },
        { transactionId: "tx-3", accountId: "demo-checking", date: "2026-01-10", name: "Netflix", amount: 15.99, category: "Entertainment" },
        { transactionId: "tx-4", accountId: "demo-checking", date: "2026-01-09", name: "Amazon.com", amount: 89.99, category: "Shopping" },
        { transactionId: "tx-5", accountId: "demo-checking", date: "2026-01-08", name: "Starbucks", amount: 6.75, category: "Food & Drink" },
        { transactionId: "tx-6", accountId: "demo-checking", date: "2026-01-07", name: "Direct Deposit - Payroll", amount: -3250.00, category: "Income" },
        { transactionId: "tx-7", accountId: "demo-checking", date: "2026-01-06", name: "Uber", amount: 24.50, category: "Transportation" },
        { transactionId: "tx-8", accountId: "demo-checking", date: "2026-01-05", name: "Target", amount: 156.32, category: "Shopping" },
        { transactionId: "tx-9", accountId: "demo-checking", date: "2026-01-04", name: "Chipotle", amount: 14.25, category: "Food & Drink" },
        { transactionId: "tx-10", accountId: "demo-checking", date: "2026-01-03", name: "Electric Company", amount: 145.00, category: "Utilities" },
    ],
    "demo-savings": [
        { transactionId: "tx-s1", accountId: "demo-savings", date: "2026-01-07", name: "Transfer from Checking", amount: -500.00, category: "Transfer" },
        { transactionId: "tx-s2", accountId: "demo-savings", date: "2026-01-01", name: "Interest Payment", amount: -28.50, category: "Income" },
        { transactionId: "tx-s3", accountId: "demo-savings", date: "2025-12-15", name: "Transfer from Checking", amount: -1000.00, category: "Transfer" },
    ],
    "demo-credit": [
        { transactionId: "tx-c1", accountId: "demo-credit", date: "2026-01-12", name: "Restaurant - The Capital Grille", amount: 245.00, category: "Food & Drink" },
        { transactionId: "tx-c2", accountId: "demo-credit", date: "2026-01-10", name: "Delta Airlines", amount: 387.00, category: "Travel" },
        { transactionId: "tx-c3", accountId: "demo-credit", date: "2026-01-08", name: "Apple Store", amount: 199.00, category: "Shopping" },
        { transactionId: "tx-c4", accountId: "demo-credit", date: "2026-01-05", name: "Spotify", amount: 10.99, category: "Entertainment" },
        { transactionId: "tx-c5", accountId: "demo-credit", date: "2026-01-03", name: "Payment - Thank You", amount: -2500.00, category: "Payment" },
    ],
};

// 28-year-old earning $85k/year, ~52% savings rate, ~$2,800/mo expenses
// 6 months of monthly savings data with realistic variation
export const DEMO_SAVINGS_RATE = {
    // Full 6-month data
    savingsRate: 52.14,
    avgMonthlyIncome: 5890,
    avgMonthlyExpenses: 2820,
    avgMonthlySavings: 3070,
    months: [
        { month: "2025-08", income: 5750, expenses: 3100, netSavings: 2650, savingsRate: 46.09 },
        { month: "2025-09", income: 5890, expenses: 2650, netSavings: 3240, savingsRate: 55.01 },
        { month: "2025-10", income: 5890, expenses: 2980, netSavings: 2910, savingsRate: 49.41 },
        { month: "2025-11", income: 6200, expenses: 3350, netSavings: 2850, savingsRate: 45.97 },
        { month: "2025-12", income: 6100, expenses: 2550, netSavings: 3550, savingsRate: 58.20 },
        { month: "2026-01", income: 5510, expenses: 2290, netSavings: 3220, savingsRate: 58.44 },
    ],
};

export const DEMO_SPENDING_BREAKDOWN = {
    totalSpending: 16920,
    transactionCount: 187,
    categories: [
        { category: "RENT_UTILITIES", displayName: "Rent & Utilities", amount: 7800, percentage: 46.1, transactionCount: 12 },
        { category: "FOOD_DRINK", displayName: "Food & Drink", amount: 2940, percentage: 17.4, transactionCount: 52 },
        { category: "TRANSPORTATION", displayName: "Transportation", amount: 1680, percentage: 9.9, transactionCount: 28 },
        { category: "SHOPPING", displayName: "Shopping", amount: 1560, percentage: 9.2, transactionCount: 18 },
        { category: "ENTERTAINMENT", displayName: "Entertainment", amount: 960, percentage: 5.7, transactionCount: 22 },
        { category: "HEALTH_FITNESS", displayName: "Health & Fitness", amount: 720, percentage: 4.3, transactionCount: 14 },
        { category: "PERSONAL_CARE", displayName: "Personal Care", amount: 480, percentage: 2.8, transactionCount: 11 },
        { category: "SUBSCRIPTIONS", displayName: "Subscriptions", amount: 360, percentage: 2.1, transactionCount: 8 },
        { category: "EDUCATION", displayName: "Education", amount: 240, percentage: 1.4, transactionCount: 6 },
        { category: "MISCELLANEOUS", displayName: "Miscellaneous", amount: 180, percentage: 1.1, transactionCount: 16 },
    ],
};

export const DEMO_FI_NUMBER = {
    fiNumber: 840000,
    annualExpenses: 33600,
    calculatedAnnualExpenses: 33600,
    annualSavings: 36840,
    withdrawalRate: 4.0,
    expensesOverridden: false,
};

export const DEMO_NET_WORTH_DIGEST = {
    previousNetWorth: 283220,
    currentNetWorth: 287450,
    totalChange: 4230,
    changePercentage: 1.49,
    previousSnapshotAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // yesterday
    currentCalculatedAt: new Date().toISOString(),
    accountChanges: [
        {
            accountId: "demo-401k",
            accountName: "Fidelity 401(k)",
            accountType: "investment",
            institutionName: "Fidelity",
            previousBalance: 143500,
            currentBalance: 145000,
            change: 1500,
            changePercentage: 1.05
        },
        {
            accountId: "demo-roth",
            accountName: "Vanguard Roth IRA",
            accountType: "investment",
            institutionName: "Vanguard",
            previousBalance: 66200,
            currentBalance: 67500,
            change: 1300,
            changePercentage: 1.96
        },
        {
            accountId: "demo-brokerage",
            accountName: "Schwab Brokerage",
            accountType: "investment",
            institutionName: "Charles Schwab",
            previousBalance: 32120,
            currentBalance: 33450,
            change: 1330,
            changePercentage: 4.14
        },
        {
            accountId: "demo-checking",
            accountName: "Primary Checking",
            accountType: "depository",
            institutionName: "Chase",
            previousBalance: 8200,
            currentBalance: 8500,
            change: 300,
            changePercentage: 3.66
        },
        {
            accountId: "demo-credit",
            accountName: "Chase Sapphire",
            accountType: "credit",
            institutionName: "Chase",
            previousBalance: 3000,
            currentBalance: 3200,
            change: -200,
            changePercentage: -6.67
        }
    ]
};
